import { useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  DataTable,
  type DataTableColumn,
  type SelectionMode,
} from "@/components/shared/DataTable";
import { SelectionModeControl } from "@/components/shared/SelectionModeControl";
import {
  useSubscriptionList,
  useCollectMonth,
  type SubscriptionRow,
} from "@/hooks/useSubscriptions";
import { SubscriptionStats } from "@/components/subscriptions/SubscriptionStats";
import { CollectMonthDialog } from "@/components/subscriptions/CollectMonthDialog";
import { formatDate, formatCurrency } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_PAGE_SIZE = 25;

const exportRows = (rows: SubscriptionRow[]) =>
  downloadCsv(
    "subscriptions",
    ["Owner", "Plan", "Period start", "Period end", "Status", "Amount"],
    rows.map((s) => [
      s.owner?.name ?? "",
      s.plan?.name ?? "",
      formatDate(s.currentPeriodStart),
      formatDate(s.currentPeriodEnd),
      String(s.status),
      s.plan ? String(s.plan.priceMonthly) : "",
    ]),
  );

export const SubscriptionsList = () => {
  const [selection, setSelection] = useState<SelectionMode>("none");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  const [collectTarget, setCollectTarget] = useState<SubscriptionRow | null>(
    null,
  );
  const { can } = useAuth();

  const { data, isLoading, error, refetch } = useSubscriptionList({
    page,
    limit,
  });
  const rows = data?.data ?? [];
  const pagination = data?.pagination;
  const collectMonth = useCollectMonth();

  const setSelectionMode = (m: SelectionMode) => {
    setSelection(m);
    setSelectedIds([]);
  };

  const columns: Array<DataTableColumn<SubscriptionRow>> = [
    {
      key: "owner",
      header: "Owner",
      render: (s) => (
        <span className="font-medium">{s.owner?.name ?? "—"}</span>
      ),
    },
    { key: "plan", header: "Plan", render: (s) => s.plan?.name ?? "—" },
    {
      key: "period",
      header: "Period",
      render: (s) => (
        <span className="text-muted-foreground">
          {formatDate(s.currentPeriodStart)} – {formatDate(s.currentPeriodEnd)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => <StatusBadge status={s.status} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (s) => (
        <span className="font-medium">
          {s.plan ? formatCurrency(s.plan.priceMonthly) : "—"}
        </span>
      ),
    },
    {
      key: "collect",
      header: "This month",
      align: "right",
      render: (s) => {
        const inactive = s.status === "CANCELLED" || s.status === "EXPIRED";
        if (inactive)
          return <span className="text-muted-foreground text-xs">—</span>;
        return s.currentMonthCollected ? (
          <span className="px-3 py-1 text-xs rounded-btn border border-green-200 bg-green-50 text-green-700 font-medium">
            Collected
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setCollectTarget(s)}
            className="px-3 py-1 text-xs rounded-btn border border-border bg-background hover:bg-muted"
          >
            Collect
          </button>
        );
      },
    },
  ];

  return (
    <AppLayout title="Subscriptions">
      <CollectMonthDialog
        open={collectTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCollectTarget(null);
        }}
        ownerName={collectTarget?.owner?.name ?? "owner"}
        loading={collectMonth.isPending}
        onConfirm={(month) => {
          if (!collectTarget) return;
          collectMonth.mutate(
            { id: collectTarget.id, month },
            {
              onSuccess: () => {
                toast.success(
                  `Month ${month} collected for ${collectTarget.owner?.name ?? "owner"}`,
                );
                setCollectTarget(null);
              },
              onError: (err) => {
                toast.error(err.message ?? "Failed to record collection");
                setCollectTarget(null);
              },
            },
          );
        }}
      />
      <SubscriptionStats />
      <ListToolbar
        onExport={
          can("subscriptions", "export") ? () => exportRows(rows) : undefined
        }
        actions={
          <SelectionModeControl value={selection} onChange={setSelectionMode} />
        }
      />

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                toast.success(`Renewed ${selectedIds.length} subscriptions`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Renew
            </button>
            <button
              type="button"
              onClick={() => {
                toast.error(`Cancelled ${selectedIds.length} subscriptions`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle="No subscriptions yet"
        emptyDescription="Subscriptions will appear here once providers enroll."
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(s) => s.id}
          selection={selection}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="subscriptions"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>
    </AppLayout>
  );
};

export default SubscriptionsList;
